import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { changePassword, getProfile, updateProfile } from "../../services/api";

const s = {
  page: { maxWidth: 860, margin: "0 auto", padding: "32px 24px" },
  title: { fontFamily: "var(--font-heading)", fontSize: 30, fontWeight: 900, color: "var(--text-primary)", marginBottom: 8 },
  hint: { fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginTop: 20 },
  card: { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 8, padding: 18, boxShadow: "var(--dashboard-card-shadow)" },
  subTitle: { fontSize: 17, fontWeight: 900, color: "var(--text-primary)", marginBottom: 10 },
  label: { display: "block", fontSize: 12, fontWeight: 900, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 12 },
  input: { width: "100%", boxSizing: "border-box", border: "1.5px solid var(--border)", borderRadius: 6, background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", padding: "10px 11px", marginTop: 6 },
  btn: { border: "none", borderRadius: 6, padding: "10px 14px", background: "var(--teal)", color: "#fff", fontWeight: 900, cursor: "pointer" },
  secondary: { border: "1px solid var(--border)", borderRadius: 6, padding: "10px 14px", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", fontWeight: 900, cursor: "pointer" },
  row: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  ok: { color: "#16a34a", fontSize: 13, fontWeight: 800, marginTop: 10 },
  err: { color: "#dc2626", fontSize: 13, fontWeight: 800, marginTop: 10 },
};

export default function Profile() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    customerName: user?.customerName || "",
    profilePhone: user?.profilePhone || "",
    profileAddress: user?.profileAddress || "",
  });
  const [passwords, setPasswords] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getProfile()
      .then((res) => {
        if (cancelled) return;
        const next = res.data.user || {};
        setProfile({
          customerName: next.customerName || next.customer_name || "",
          profilePhone: next.profilePhone || "",
          profileAddress: next.profileAddress || "",
        });
        updateUser(next);
      })
      .catch(() => {
        if (!cancelled) setProfileMessage({ type: "err", text: "Could not load the latest profile information." });
      });
    return () => { cancelled = true; };
  }, [updateUser]);

  async function saveProfile(event) {
    event.preventDefault();
    setLoading(true);
    setProfileMessage(null);
    try {
      const res = await updateProfile(profile);
      updateUser(res.data.user);
      setProfileMessage({ type: "ok", text: "Profile updated." });
    } catch (err) {
      setProfileMessage({ type: "err", text: err.response?.data?.error || "Failed to update profile." });
    } finally {
      setLoading(false);
    }
  }

  async function savePassword(event) {
    event.preventDefault();
    setPasswordMessage(null);
    if (passwords.newPassword.length < 8) {
      setPasswordMessage({ type: "err", text: "Password must be at least 8 characters." });
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordMessage({ type: "err", text: "New passwords do not match." });
      return;
    }
    setLoading(true);
    try {
      await changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordMessage({ type: "ok", text: "Password updated." });
    } catch (err) {
      setPasswordMessage({ type: "err", text: err.response?.data?.error || "Failed to update password." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={s.page}>
      <div style={{ ...s.row, justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <h1 style={s.title}>Profile</h1>
          <div style={s.hint}>Manage your dashboard account information and password.</div>
        </div>
        <button type="button" style={s.secondary} onClick={() => navigate(["admin", "collaborator"].includes(user?.role) ? "/admin" : "/dashboard")}>
          Back
        </button>
      </div>

      <div style={s.grid}>
        <form style={s.card} onSubmit={saveProfile}>
          <div style={s.subTitle}>Account Information</div>
          <div style={s.hint}>These fields belong to your dashboard account. Lab-submitted report details remain tied to the verified report snapshot.</div>
          <label style={s.label}>Email</label>
          <input style={{ ...s.input, opacity: 0.8 }} value={user?.email || ""} disabled />
          <label style={s.label}>Name</label>
          <input style={s.input} value={profile.customerName} onChange={(event) => setProfile((current) => ({ ...current, customerName: event.target.value }))} placeholder="Your name" />
          <label style={s.label}>Phone</label>
          <input style={s.input} value={profile.profilePhone} onChange={(event) => setProfile((current) => ({ ...current, profilePhone: event.target.value }))} placeholder="Best contact phone" />
          <label style={s.label}>Address</label>
          <textarea style={{ ...s.input, minHeight: 96, resize: "vertical" }} value={profile.profileAddress} onChange={(event) => setProfile((current) => ({ ...current, profileAddress: event.target.value }))} placeholder="Mailing address" />
          <button type="submit" style={{ ...s.btn, marginTop: 14 }} disabled={loading}>{loading ? "Saving..." : "Save Profile"}</button>
          {profileMessage && <div style={profileMessage.type === "ok" ? s.ok : s.err}>{profileMessage.text}</div>}
        </form>

        <form style={s.card} onSubmit={savePassword}>
          <div style={s.subTitle}>Change Password</div>
          <div style={s.hint}>Enter your current password before setting a new one.</div>
          <label style={s.label}>Current Password</label>
          <input style={s.input} type="password" value={passwords.currentPassword} onChange={(event) => setPasswords((current) => ({ ...current, currentPassword: event.target.value }))} autoComplete="current-password" />
          <label style={s.label}>New Password</label>
          <input style={s.input} type="password" value={passwords.newPassword} onChange={(event) => setPasswords((current) => ({ ...current, newPassword: event.target.value }))} autoComplete="new-password" />
          <label style={s.label}>Confirm New Password</label>
          <input style={s.input} type="password" value={passwords.confirmPassword} onChange={(event) => setPasswords((current) => ({ ...current, confirmPassword: event.target.value }))} autoComplete="new-password" />
          <button type="submit" style={{ ...s.btn, marginTop: 14 }} disabled={loading}>{loading ? "Saving..." : "Change Password"}</button>
          {passwordMessage && <div style={passwordMessage.type === "ok" ? s.ok : s.err}>{passwordMessage.text}</div>}
        </form>
      </div>
    </div>
  );
}
